import {
  Button,
  Checkbox,
  Input,
  InputType,
  Tooltip,
  Typography,
} from '../../components/primitives';
import { ExpiredRegistrationStatus } from '../../components/text/ExpiredRegistrationStatus';
import {
  TermsAndConditions,
  TermsAndConditionsEnum,
} from '../../components/text/TermsAndConditions';
import Alert from '../../components/ui/Alert';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import {
  urlParamMessageTypeEnum,
  useUrlParamsMessage,
} from '../../hooks/urlParamsMessageHook';
import { Icons } from '../../icons';
import { SearchParamsBuilder, testId, toParams } from '../../utils';
import { storeTokens } from '../../utils/auth/session-storage';
import {
  MAIN_REDIRECT,
  PASSWORD_MAX,
  PASSWORD_MIN,
} from '../../utils/constants';
import { PageCard } from './PageCard';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cornerstone } from '@rpi/api';
import { useTranslation, Trans } from '@rpi/i18n';
import { AxiosResponse } from 'axios';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

export const SignInRoute: React.FC = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.auth' });

  const [searchParams] = useSearchParams();

  const { urlParamMessage, clearUrlParamMessage } = useUrlParamsMessage();

  const SignInSchema = z
    .object({
      email: z.string().email(`${t('email_requirements')}`),
      password: z
        .string()
        .min(PASSWORD_MIN, `${t('minPassword', { minChar: PASSWORD_MIN })}`),
    })
    .required();

  type SignInSchema = z.infer<typeof SignInSchema>;

  const form = useForm<SignInSchema>({
    resolver: zodResolver(SignInSchema),
    defaultValues: {
      email: searchParams.get('email') ?? '',
      password: '',
    },
    mode: 'onBlur',
  });

  const navigate = useNavigate();

  const { isError, error, isLoading, mutate } = Cornerstone.useLogin({
    mutation: {
      onSuccess(loginResponse: AxiosResponse) {
        storeTokens(
          loginResponse.data.accessToken,
          loginResponse.data.refreshToken,
        );
        navigate(searchParams.get('redirect') || MAIN_REDIRECT);
      },
      onError() {
        clearUrlParamMessage();
      },
    },
  });

  function onSubmit(data: SignInSchema) {
    mutate({ data: { email: data.email, password: data.password } });
  }

  const mutationData = error?.response?.data as any;

  const alertText = useMemo(() => {
    if (urlParamMessage?.message === urlParamMessageTypeEnum.password_reset)
      return t('password_reset_success');
    if (
      urlParamMessage?.message === urlParamMessageTypeEnum.registration_success
    )
      return t('registration_success');
    if (
      mutationData?.errorCode ===
      Cornerstone.ErrorCode.MISSING_2_STEP_REGISTRATION_CONFIRMATION
    ) {
      return <ExpiredRegistrationStatus email={form.getValues().email} />;
    }
    if (mutationData?.errorCode === Cornerstone.ErrorCode.BAD_CREDENTIALS) {
      return t('login_error');
    }
    if (isError) return t('generic_error');
    return null;
  }, [urlParamMessage, isError, mutationData, t, form]);

  return (
    <PageCard>
      <Form {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          {alertText && (
            <Alert
              data-testid="signin-alert"
              variant={
                error ||
                mutationData?.errorCode ===
                  Cornerstone.ErrorCode.MISSING_2_STEP_REGISTRATION_CONFIRMATION
                  ? 'error'
                  : 'success'
              }
            >
              {alertText}
            </Alert>
          )}
          <Typography variant="title-default" as="h1" className="text-center">
            {t('login_title')}
          </Typography>
          <RequiredFieldIndicator />
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel {...testId('email', 'label')}>
                  <Typography variant="form-label">
                    <Trans
                      i18nKey="route.auth.email"
                      components={{
                        'required-indicator': <Icons.Indicator />,
                      }}
                    />
                  </Typography>
                </FormLabel>
                <FormControl>
                  <Input
                    {...testId('email')}
                    type={InputType.EMAIL}
                    placeholder=""
                    {...field}
                    invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage {...testId('email', 'message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel {...testId('password', 'label')}>
                  <Typography variant="form-label">
                    <Trans
                      i18nKey="route.auth.password"
                      components={{
                        'required-indicator': <Icons.Indicator />,
                      }}
                    />
                  </Typography>
                </FormLabel>
                <FormControl>
                  <Input
                    {...testId('password')}
                    type={InputType.PASSWORD}
                    placeholder=""
                    {...field}
                    maxLength={PASSWORD_MAX}
                    invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage {...testId('password', 'message')} />
              </FormItem>
            )}
          />
          <Typography variant="body">
            <Link
              to={toParams(
                '/auth/forgot-password',
                new SearchParamsBuilder(searchParams).add(
                  'email',
                  form.getValues().email,
                ),
              )}
            >
              {t('forgot_password_link')}
            </Link>
          </Typography>
          <div className="flex gap-1">
            <Checkbox {...testId('keep_logged_in', 'checkbox')} />
            <Typography variant="body">{t('login_keep_logged_in')}</Typography>
            <Tooltip
              {...testId('keep_logged_in', 'tooltip')}
              content={
                <Typography variant="footnote">
                  {t('login_keep_logged_in_tooltip')}
                </Typography>
              }
            />
          </div>
          <TermsAndConditions termsEnum={TermsAndConditionsEnum.SIGNIN} />
          <Button
            {...testId('submit')}
            type="submit"
            className="w-full"
            loading={isLoading}
          >
            {t('login')}
          </Button>
          <Typography variant="body" className="text-center">
            <Trans
              i18nKey="route.auth.login_need_account"
              components={{
                'signup-link': (
                  <Link
                    to={toParams(
                      '/auth/sign-up',
                      new SearchParamsBuilder(searchParams).add(
                        'email',
                        form.getValues().email,
                      ),
                    )}
                  />
                ),
              }}
            />
          </Typography>
        </form>
      </Form>
    </PageCard>
  );
};
